import React from 'react';
import { motion } from 'framer-motion';
import Section from '../common/Section';
import ParallaxText from '../ui/ParallaxText';
import { PROFILE_CONTENT } from '../../utils/constants';
import { MapPin, Sparkles, Target } from 'lucide-react';

const Profile: React.FC = () => {
    const containerVariants = {
        hidden: { opacity: 0 },
        visible: {
            opacity: 1,
            transition: {
                staggerChildren: 0.2,
                delayChildren: 0.3,
            },
        },
    };

    const itemVariants = {
        hidden: { opacity: 0, y: 30 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.6,
                ease: [0.19, 1, 0.22, 1],
            },
        },
    };

    return (
        <Section id="profile" className="relative bg-gradient-to-b from-white via-sky-50/30 to-white">
            {/* Background decoration */}
            <div className="absolute inset-0 overflow-hidden">
                <div className="absolute -top-40 -right-40 w-80 h-80 bg-sky-200/20 rounded-full filter blur-3xl" />
                <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-purple-200/20 rounded-full filter blur-3xl" />
            </div>

            <div className="relative z-10 max-w-4xl mx-auto">
                <ParallaxText offset={30}>
                    <motion.div
                        initial={{ opacity: 0, y: -20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="text-center mb-16"
                    >
                        <h2 className="h2-display text-slate-900 mb-4">
                            <span className="text-gradient">About Me</span>
                        </h2>
                        <div className="w-24 h-1 mx-auto bg-gradient-to-r from-sky-400 to-purple-400 rounded-full" />
                    </motion.div>
                </ParallaxText>

                <motion.div
                    variants={containerVariants}
                    initial="hidden"
                    whileInView="visible"
                    viewport={{ once: true }}
                    className="space-y-12"
                >
                    {/* Profile Header */}
                    <motion.div variants={itemVariants} className="text-center space-y-4">
                        <div className="inline-flex items-center gap-2 text-slate-600 mb-4">
                            <MapPin size={20} className="text-sky-500" />
                            <span className="jp-text">{PROFILE_CONTENT.location}</span>
                        </div>

                        <div className="space-y-2">
                            <h3 className="text-3xl font-bold text-slate-900 jp-heading">
                                {PROFILE_CONTENT.name}
                            </h3>
                            <p className="text-xl text-slate-600">{PROFILE_CONTENT.nameEn}</p>
                        </div>
                    </motion.div>

                    {/* Main Description */}
                    <motion.div
                        variants={itemVariants}
                        className="bg-white/80 backdrop-blur-sm rounded-2xl p-8 md:p-10 shadow-xl shadow-sky-100/50 border border-sky-100"
                    >
                        <div className="flex items-start gap-4 mb-6">
                            <div className="p-3 rounded-lg bg-gradient-to-br from-sky-400 to-sky-500 text-white">
                                <Sparkles size={24} />
                            </div>
                            <div className="flex-1">
                                <h4 className="text-xl font-bold text-slate-900 mb-4">Experience & Activities</h4>
                                <p className="text-slate-700 leading-relaxed jp-text whitespace-pre-line">
                                    {PROFILE_CONTENT.mainDescription}
                                </p>
                            </div>
                        </div>

                        {/* VRCi Experience - Special Highlight */}
                        <motion.div
                            className="mt-8 p-6 rounded-xl bg-gradient-to-r from-sky-50 to-purple-50 border border-sky-200"
                            whileHover={{ scale: 1.02 }}
                            transition={{ duration: 0.3 }}
                        >
                            <div className="flex items-center gap-3 mb-3">
                                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 flex items-center justify-center text-white font-bold">
                                    VR
                                </div>
                                <h5 className="text-lg font-bold text-slate-900">Recent Achievement</h5>
                            </div>
                            <p className="text-slate-700 jp-text">
                                {PROFILE_CONTENT.additionalSkill}
                            </p>
                        </motion.div>
                    </motion.div>

                    {/* Future Vision */}
                    <motion.div
                        variants={itemVariants}
                        className="relative"
                    >
                        <div className="absolute inset-0 bg-gradient-to-r from-sky-400/10 to-purple-400/10 rounded-2xl transform rotate-1" />
                        <div className="relative bg-white rounded-2xl p-8 md:p-10 shadow-xl border border-slate-100">
                            <div className="flex items-start gap-4">
                                <div className="p-3 rounded-lg bg-gradient-to-br from-purple-400 to-pink-400 text-white">
                                    <Target size={24} />
                                </div>
                                <div className="flex-1">
                                    <h4 className="text-xl font-bold text-slate-900 mb-4">Future Vision</h4>
                                    <p className="text-slate-700 leading-relaxed jp-text whitespace-pre-line">
                                        {PROFILE_CONTENT.futureVision}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </motion.div>
                </motion.div>
            </div>
        </Section>
    );
};

export default Profile;